import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext({
    items: [],
    addItem: () => {},
    removeItem: () => {},
    updateQty: () => {},
    clear: () => {},
    itemCount: 0,
    subtotal: 0,
});

const STORAGE_KEY = "cc_cart_v2";

/**
 * Compute the best per-unit price given a base price and optional bulk tiers.
 * A tier {qty:4, price:55} means "buy 4 for $55 total" → per-unit = 13.75.
 * When customer qty >= tier.qty, they get that per-unit rate.
 * Returns the lowest per-unit price applicable to the given quantity.
 */
export function effectiveUnitPrice(base, tiers, qty) {
    let best = Number(base) || 0;
    if (!Array.isArray(tiers)) return best;
    for (const t of tiers) {
        const tQty = Number(t?.qty) || 0;
        const tPrice = Number(t?.price) || 0;
        if (tQty <= 0 || tPrice <= 0 || qty < tQty) continue;
        const perUnit = tPrice / tQty;
        if (perUnit < best) best = perUnit;
    }
    return best;
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {}
    }, [items]);

    const addItem = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const found = prev.find((p) => p.product_id === product.id);
            if (found) {
                return prev.map((p) =>
                    p.product_id === product.id ? { ...p, qty: p.qty + qty } : p
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    slug: product.slug,
                    name: product.name,
                    image_url: product.image_url,
                    price: product.price,
                    price_tiers: product.price_tiers || [],
                    category: product.category,
                    size_label: product.size_label || null,
                    option: product.option || null,
                    qty,
                },
            ];
        });
    }, []);

    const removeItem = useCallback((product_id) => {
        setItems((prev) => prev.filter((p) => p.product_id !== product_id));
    }, []);

    const updateQty = useCallback((product_id, qty) => {
        if (qty <= 0) {
            setItems((prev) => prev.filter((p) => p.product_id !== product_id));
            return;
        }
        setItems((prev) =>
            prev.map((p) => (p.product_id === product_id ? { ...p, qty } : p))
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const itemCount = useMemo(() => items.reduce((s, it) => s + it.qty, 0), [items]);
    const subtotal = useMemo(
        () => items.reduce((s, it) => s + it.qty * effectiveUnitPrice(it.price, it.price_tiers, it.qty), 0),
        [items]
    );

    return (
        <CartContext.Provider
            value={{ items, addItem, removeItem, updateQty, clear, itemCount, subtotal }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}
